/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from "i18next";
import StatusBadge from "@/shared/ui/components/basic/StatusBadge";
import type { StatusBadgeTone } from "@/shared/ui/components/basic/StatusBadge";
import { CalendarIcon, ChatBubbleIcon, EnvelopeClosedIcon, MobileIcon, Text, TrashIcon, UnstyledButton } from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { memberName } from '../../utils/broker-breakdown';
import type { HistoryEvent } from "@services/requests/housien-services/ClientInsightsService";
import { cn } from "@/shared/ui/utils/tailwind";
import { formatLocalizedDate } from '@/shared/utils/date';
import { useDealStageLabels, type DealStage } from '@/features/deals/constants/deal-stages';

const EVENT_TONES: Record<string, StatusBadgeTone> = {
  visit: "brand",
  negotiation: "warning",
  interest: "success",
  interaction: "info",
};

/**
 * Channel glyphs. WhatsApp gets its own mark rather than a speech bubble --
 * the generic bubble read as "comment", not as the channel the contact
 * actually happened on.
 */
/*
 * Sized by class rather than by width/height attributes: `WhatsAppIcon`
 * forwards only `className`, so the attributes it dropped left an unsized
 * 24-viewBox svg that the chip cropped.
 */
const ICON_CLASS = "block h-4 w-4 shrink-0";

const CHANNEL_ICONS: Record<string, () => JSX.Element> = {
  whatsapp: () => <WhatsAppIcon className={ICON_CLASS} />,
  email: () => <EnvelopeClosedIcon className={ICON_CLASS} />,
  phone: () => <MobileIcon className={ICON_CLASS} />,
};

/** Events with no channel of their own are stamped by what they are. */
const EVENT_ICONS: Record<string, () => JSX.Element> = {
  visit: () => <CalendarIcon className={ICON_CLASS} />,
};

const DELETABLE = new Set(["interaction"]);

/**
 * Marks the one-time "a human agent opened this conversation" interaction
 * SocialMessageService stamps on a lead's first social reply (see
 * `_log_conversation_started` in `social_message_service.py`) -- distinct
 * enough that no broker would type it into a real comment by hand.
 */
const CONVERSATION_STARTED_MARKER = "__system_conversation_started__";

/**
 * Resolves the primary display text for a history event.
 *
 * A `negotiation` event's `status` is a deal stage (the backend sets it
 * straight from `deal.stage`), not the interaction status vocabulary --
 * translating it against `profile.history.status.*` left every stage
 * that vocabulary doesn't happen to share (`closed_won`, `paused`,
 * `visit_scheduled`, ...) on screen as the raw wire value.
 *
 * @param event - The history event to describe.
 * @param labels - The event-type label map.
 * @param t - The translation function for status labels.
 * @param dealStageLabels - Localized labels for the deal-stage vocabulary.
 * @param channelLabel - Localized channel name, for the system-generated entries.
 * @returns The event detail, translated status, or type label.
 */
function primaryText(
  event: HistoryEvent,
  labels: Record<string, string>,
  t: TFunction,
  dealStageLabels: Record<DealStage, string>,
  channelLabel: string | null,
): string {
  if (event.detail === CONVERSATION_STARTED_MARKER) {
    return t('profile.history.system.conversationStarted', 'Iniciou uma conversa pelo {{channel}}', {
      channel: channelLabel ?? event.channel ?? '',
    });
  }
  if (event.detail?.trim()) return event.detail.trim();
  const stage = negotiationStage(event, dealStageLabels);
  if (stage) return stage;
  if (event.status) return t(`profile.history.status.${event.status}`, event.status);
  return labels[event.event_type] || event.event_type;
}

/**
 * A negotiation event names a pipeline stage, which has its own vocabulary --
 * the generic status labels would mistranslate it.
 *
 * @param event - The history event to describe.
 * @param dealStageLabels - Localized labels for the deal-stage vocabulary.
 * @returns The stage label, or null when this is not a staged negotiation.
 */
function negotiationStage(
  event: HistoryEvent,
  dealStageLabels: Record<DealStage, string>,
): string | null {
  if (event.event_type !== 'negotiation' || !event.status) return null;
  return dealStageLabels[event.status as DealStage] ?? event.status;
}

/**
 * One history entry, rendered on the same row surface as the visit,
 * property-of-interest and proposal rows: body text and meta on the left, the
 * type badge trailing, and the delete action only where the entry is ours to
 * delete.
 *
 * @param props - The event, labels, translation function, and delete callback.
 * @returns The rendered interaction event card.
 */
export function InteractionEventCard({
  event,
  labels,
  t,
  onDelete,
}: {
  event: HistoryEvent;
  labels: Record<string, string>;
  t: TFunction;
  onDelete?: (interactionId: string) => void;
}) {
  const dealStageLabels = useDealStageLabels();
  const members = useAgency((s) => s.agency?.members) ?? [];
  const renderIcon = (event.channel ? CHANNEL_ICONS[event.channel] : undefined)
    ?? EVENT_ICONS[event.event_type]
    ?? (() => <ChatBubbleIcon className={ICON_CLASS} />);
  const tone = EVENT_TONES[event.event_type] || "neutral";
  const canDelete = DELETABLE.has(event.event_type) && Boolean(onDelete);
  const dateStr = event.date
    ? formatLocalizedDate(event.date, undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  // The wire carries the broker's e-mail; the reader knows colleagues by name.
  const author = event.created_by ? memberName(members, event.created_by) : "";
  const channelLabel = event.channel
    ? t(`profile.history.form.channels.${event.channel}`, event.channel)
    : null;
  const body = primaryText(event, labels, t, dealStageLabels, channelLabel);
  // One line, ordered by what the reader scans for: when, then how, then who.
  const meta = [dateStr, channelLabel, author].filter(Boolean).join(" · ");

  return (
    <div
      data-testid={`interaction-event-${event.entity_id}`}
      className="flex items-center gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-ultra-subtle)]"
    >
      <Text
        component="span"
        aria-hidden
        title={channelLabel ?? undefined}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full leading-none bg-[var(--surface-secondary)] text-[var(--text-secondary)]"
      >
        {renderIcon()}
      </Text>

      <div className="flex min-w-0 flex-1 flex-col">
        <Text className="m-0 truncate text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)] text-[var(--text-primary)]">
          {body}
        </Text>
        <Text
          component="span"
          data-testid={`interaction-meta-${event.entity_id}`}
          className="truncate text-[length:var(--text-micro)] text-[var(--text-tertiary)]"
        >
          {meta}
        </Text>
      </div>

      <div className="flex shrink-0 items-center gap-[var(--space-2)]">
        <StatusBadge tone={tone}>{labels[event.event_type] || event.event_type}</StatusBadge>
        {canDelete ? (
          <UnstyledButton
            type="button"
            data-testid={`delete-interaction-${event.entity_id}`}
            aria-label={t("profile.history.delete", "Delete")}
            onClick={() => onDelete?.(event.entity_id)}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-(--radius-md)",
              "text-[var(--text-tertiary)] hover:bg-[var(--surface-hover)] hover:text-[var(--status-error-text)]",
              "cursor-pointer transition-all duration-(--duration-fast)",
            )}
          >
            <TrashIcon width={14} height={14} />
          </UnstyledButton>
        ) : null}
      </div>
    </div>
  );
}
